import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import './ExamManagement.css';

const ExamManagement = ({ curso }) => {
    const [questions, setQuestions] = useState([]);
    const [examStats, setExamStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [showQuestionForm, setShowQuestionForm] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [validationResult, setValidationResult] = useState(null);

    const [questionForm, setQuestionForm] = useState({
        question_text: '',
        options: [
            { option_text: '', is_correct: false },
            { option_text: '', is_correct: false }
        ]
    });

    useEffect(() => {
        if (curso?.id) {
            loadExamData();
        }
    }, [curso]);

    const loadExamData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE_URL}/admin/cursos/${curso.id}/exam-overview`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            
            setQuestions(response.data.questions);
            setExamStats(response.data.exam_stats);
        } catch (error) {

            alert('Erro ao carregar dados do exame');
        } finally {
            setLoading(false);
        }
    };

    const validateExam = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE_URL}/admin/cursos/${curso.id}/validate-exam`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            
            setValidationResult(response.data);
        } catch (error) {

            alert('Erro ao validar exame');
        }
    };

    const resetQuestionForm = () => {
        setQuestionForm({
            question_text: '',
            options: [
                { option_text: '', is_correct: false },
                { option_text: '', is_correct: false }
            ]
        });
        setEditingQuestion(null);
        setShowQuestionForm(false);
    };

    const handleQuestionFormChange = (field, value) => {
        setQuestionForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleOptionChange = (index, field, value) => {
        setQuestionForm(prev => ({
            ...prev,
            options: prev.options.map((option, i) => 
                i === index ? { ...option, [field]: value } : option
            )
        }));
    };

    const addOption = () => {
        if (questionForm.options.length < 5) {
            setQuestionForm(prev => ({
                ...prev,
                options: [...prev.options, { option_text: '', is_correct: false }]
            }));
        }
    };

    const removeOption = (index) => {
        if (questionForm.options.length > 2) {
            setQuestionForm(prev => ({
                ...prev,
                options: prev.options.filter((_, i) => i !== index)
            }));
        }
    };

    const setCorrectAnswer = (index) => {
        setQuestionForm(prev => ({
            ...prev,
            options: prev.options.map((option, i) => ({
                ...option,
                is_correct: i === index
            }))
        }));
    };

    const saveQuestion = async () => {
        try {
            // Validação básica
            if (!questionForm.question_text.trim()) {
                alert('Por favor, insira o texto da questão');
                return;
            }

            if (questionForm.options.some(opt => !opt.option_text.trim())) {
                alert('Todas as opções devem ter texto');
                return;
            }

            if (!questionForm.options.some(opt => opt.is_correct)) {
                alert('Marque uma opção como correta');
                return;
            }

            const token = localStorage.getItem('token');
            let response;

            if (editingQuestion) {
                // Atualizar questão existente
                response = await axios.put(
                    `${API_BASE_URL}/admin/questions/${editingQuestion.id}`,
                    questionForm,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
            } else {
                // Criar nova questão
                response = await axios.post(
                    `${API_BASE_URL}/admin/cursos/${curso.id}/questions`,
                    questionForm,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
            }

            alert(response.data.message);
            resetQuestionForm();
            loadExamData();
        } catch (error) {

            if (error.response?.data?.errors) {
                const errors = Object.values(error.response.data.errors).flat();
                alert('Erros de validação:\n' + errors.join('\n'));
            } else {
                alert('Erro ao salvar questão: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const editQuestion = (question) => {
        setEditingQuestion(question);
        setQuestionForm({
            question_text: question.question_text,
            options: question.options.map(opt => ({
                id: opt.id,
                option_text: opt.option_text,
                is_correct: opt.is_correct
            }))
        });
        setShowQuestionForm(true);
    };

    const deleteQuestion = async (questionId) => {
        if (!window.confirm('Tem certeza que deseja excluir esta questão?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE_URL}/admin/questions/${questionId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            alert('Questão excluída com sucesso!');
            loadExamData();
        } catch (error) {

            alert('Erro ao excluir questão');
        }
    };

    if (loading) {
        return <div className="exam-management-loading">Carregando dados do exame...</div>;
    }

    return (
        <div className="exam-management">
            <div className="exam-header">
                <h2>Gestão de Prova - {curso?.title}</h2>
                <div className="exam-actions">
                    <button 
                        className="btn-validate"
                        onClick={validateExam}
                    >
                        Validar Prova
                    </button>
                    <button 
                        className="btn-add-question"
                        onClick={() => setShowQuestionForm(true)}
                    >
                        Nova Questão
                    </button>
                </div>
            </div>

            {/* Estatísticas do Exame */}
            <div className="exam-stats">
                <div className="stat-card">
                    <span className="stat-number">{examStats.total_questions || 0}</span>
                    <span className="stat-label">Total de Questões</span>
                </div>
                <div className="stat-card">
                    <span className="stat-number">{examStats.questions_with_correct_answers || 0}</span>
                    <span className="stat-label">Questões Válidas</span>
                </div>
                <div className="stat-card">
                    <span className="stat-number">{examStats.incomplete_questions || 0}</span>
                    <span className="stat-label">Questões Incompletas</span>
                </div>
                <div className={`stat-card ${examStats.total_questions >= 3 ? 'stat-success' : 'stat-warning'}`}>
                    <span className="stat-number">{examStats.minimum_questions_required || 3}</span>
                    <span className="stat-label">Mínimo Necessário</span>
                </div>
            </div>

            {/* Resultado da Validação */}
            {validationResult && (
                <div className={`validation-result ${validationResult.is_valid ? 'valid' : 'invalid'}`}>
                    <h3>{validationResult.message}</h3>
                    {validationResult.errors?.length > 0 && (
                        <div className="validation-errors">
                            <h4>Erros encontrados:</h4>
                            <ul>
                                {validationResult.errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {validationResult.warnings?.length > 0 && (
                        <div className="validation-warnings">
                            <h4>Avisos:</h4>
                            <ul>
                                {validationResult.warnings.map((warning, index) => (
                                    <li key={index}>{warning}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Lista de Questões */}
            <div className="questions-list">
                <h3>Questões do Exame</h3>
                {questions.length === 0 ? (
                    <div className="no-questions">
                        <p>Nenhuma questão cadastrada ainda.</p>
                        <p>Clique em "Nova Questão" para começar a criar o exame.</p>
                    </div>
                ) : (
                    questions.map((question, index) => (
                        <div key={question.id} className="question-card">
                            <div className="question-header">
                                <span className="question-number">Questão {index + 1}</span>
                                <div className="question-actions">
                                    <button 
                                        className="btn-edit"
                                        onClick={() => editQuestion(question)}
                                    >
                                        Editar
                                    </button>
                                    <button 
                                        className="btn-delete"
                                        onClick={() => deleteQuestion(question.id)}
                                    >
                                        Excluir
                                    </button>
                                </div>
                            </div>
                            <div className="question-text">
                                {question.question_text}
                            </div>
                            <div className="question-options">
                                {question.options.map((option, optIndex) => (
                                    <div 
                                        key={option.id} 
                                        className={`option-item ${option.is_correct ? 'correct-option' : ''}`}
                                    >
                                        <span className="option-letter">
                                            {String.fromCharCode(65 + optIndex)})
                                        </span>
                                        <span className="option-text">{option.option_text}</span>
                                        {option.is_correct && <span className="correct-indicator">✓</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Formulário de Questão */}
            {showQuestionForm && (
                <div className="question-form-modal">
                    <div className="question-form">
                        <div className="form-header">
                            <h3>{editingQuestion ? 'Editar Questão' : 'Nova Questão'}</h3>
                            <button 
                                className="btn-close"
                                onClick={resetQuestionForm}
                            >
                                ×
                            </button>
                        </div>

                        <div className="form-group">
                            <label>Texto da Questão:</label>
                            <textarea
                                value={questionForm.question_text}
                                onChange={(e) => handleQuestionFormChange('question_text', e.target.value)}
                                placeholder="Digite o texto da questão..."
                                rows={4}
                            />
                        </div>

                        <div className="form-group">
                            <label>Opções de Resposta:</label>
                            {questionForm.options.map((option, index) => (
                                <div key={index} className="option-input">
                                    <div className="option-controls">
                                        <input
                                            type="text"
                                            value={option.option_text}
                                            onChange={(e) => handleOptionChange(index, 'option_text', e.target.value)}
                                            placeholder={`Opção ${String.fromCharCode(65 + index)}`}
                                        />
                                        <label className="correct-checkbox">
                                            <input
                                                type="radio"
                                                name="correct_answer"
                                                checked={option.is_correct}
                                                onChange={() => setCorrectAnswer(index)}
                                            />
                                            Correta
                                        </label>
                                        {questionForm.options.length > 2 && (
                                            <button 
                                                type="button"
                                                className="btn-remove-option"
                                                onClick={() => removeOption(index)}
                                            >
                                                Remover
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            
                            {questionForm.options.length < 5 && (
                                <button 
                                    type="button"
                                    className="btn-add-option"
                                    onClick={addOption}
                                >
                                    Adicionar Opção
                                </button>
                            )}
                        </div>

                        <div className="form-actions">
                            <button 
                                className="btn-cancel"
                                onClick={resetQuestionForm}
                            >
                                Cancelar
                            </button>
                            <button 
                                className="btn-save"
                                onClick={saveQuestion}
                            >
                                {editingQuestion ? 'Atualizar' : 'Salvar'} Questão
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExamManagement;
